// useNewerBuild — the tab notices its own staleness.
//
// ── WHY ─────────────────────────────────────────────────────────────────────
// This is a single-page app. Navigating between screens does NOT fetch new
// JavaScript, so a tab left open across a deploy keeps running the OLD build
// indefinitely — with no visible sign. On 22 Aug that booked 207,955 USDT
// through a superseded code path; on 14 Sept the operator's Safari tab kept
// the morning's code all day, so every deploy of the sweep looked like "same
// issue". The books are the server's business; the CONSOLE's business is to
// say, and then to act, when a newer one is up.
//
// ── HOW ─────────────────────────────────────────────────────────────────────
// vite.config.ts bakes VITE_BUILD_ID (the short git commit) into the bundle
// and writes dist/version.json {"build": "<id>"} beside it, served with
// Cache-Control: no-store (public/_headers). This hook fetches that file on
// mount, every 60 s and on window focus; when its build differs from the one
// this bundle carries, `newer` is true. The old check re-fetched index.html
// and compared the entry-script hash — right in principle, but a cached
// index.html could hand the tab its own hash back, and it was a banner alone.
//
// What happens next is ConsoleShell's: a bar with a Reload button, and a
// reload by itself on the next route change (never mid-screen — there is no
// global dirty flag, so a form is never wiped from under the operator).
//
// A failed fetch (offline, a dev server with no version.json) says nothing:
// null is "could not read", never "current".

import { useEffect, useState } from 'react';

const CHECK_MS = 60_000;

/** The build this tab is running. */
export const BUILD_ID: string = import.meta.env.VITE_BUILD_ID;

async function deployedBuild(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { build?: unknown };
    return typeof body.build === 'string' && body.build ? body.build : null;
  } catch {
    return null;
  }
}

export function useNewerBuild(): boolean {
  const [newer, setNewer] = useState(false);
  useEffect(() => {
    if (!BUILD_ID) return;                        // nothing to compare against
    let done = false;
    const check = async () => {
      if (done) return;
      const live = await deployedBuild();
      if (live && live !== BUILD_ID) { done = true; setNewer(true); }
    };
    const timer = setInterval(check, CHECK_MS);
    window.addEventListener('focus', check);
    void check();
    return () => { done = true; clearInterval(timer); window.removeEventListener('focus', check); };
  }, []);
  return newer;
}
